// ============================================================================
// STORE.PACKS.JS — Specialist Pack Builder Persistence Layer
//
// CRUD operations for specialist-authored Packs.
// Canonical path: tenants/{tenantId}/specialties/{specialtyId}/packs/{packDocId}
// Pack doc ID convention: {packId}__{versionId}
//
// Lifecycle: draft → active → archived
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';

const getDb = () => getFirestore(getApp());

const PACK_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived',
};

const generatePackId = () => {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `PACK-${ts}-${rand}`;
};

// ============================================================================
// SEMVER HELPERS (pure — no Firestore; safe to unit-test under node)
// ============================================================================

const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)$/;

/** Parse 'a.b.c' → { major, minor, patch } | null if invalid. */
export const parseSemver = (v) => {
  if (typeof v !== 'string') return null;
  const m = SEMVER_RE.exec(v.trim());
  if (!m) return null;
  return { major: Number(m[1]), minor: Number(m[2]), patch: Number(m[3]) };
};

/** compareSemver(a, b) → -1 if a<b, 0 if equal, 1 if a>b. Throws on invalid input. */
export const compareSemver = (a, b) => {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (!pa) throw new Error(`Invalid semver: "${a}"`);
  if (!pb) throw new Error(`Invalid semver: "${b}"`);
  if (pa.major !== pb.major) return pa.major < pb.major ? -1 : 1;
  if (pa.minor !== pb.minor) return pa.minor < pb.minor ? -1 : 1;
  if (pa.patch !== pb.patch) return pa.patch < pb.patch ? -1 : 1;
  return 0;
};

/** autoBumpPatch('1.0.0') → '1.0.1'. Throws on invalid input. */
export const autoBumpPatch = (v) => {
  const p = parseSemver(v);
  if (!p) throw new Error(`Cannot bump invalid version: "${v}"`);
  return `${p.major}.${p.minor}.${p.patch + 1}`;
};

/**
 * Choose the new version when republishing an active pack.
 *   - requestedVersion is used ONLY if it is valid semver AND strictly greater
 *     than currentVersion (preserves a deliberate manual major/minor bump).
 *   - otherwise auto-bump the patch of currentVersion.
 * Throws if currentVersion itself is not valid semver.
 */
export const selectNewVersionId = (currentVersion, requestedVersion) => {
  if (!parseSemver(currentVersion)) {
    throw new Error(`Current pack version "${currentVersion}" is not valid semver`);
  }
  if (
    requestedVersion &&
    parseSemver(requestedVersion) &&
    compareSemver(requestedVersion, currentVersion) > 0
  ) {
    return requestedVersion.trim();
  }
  return autoBumpPatch(currentVersion);
};

// ============================================================================
// CREATE DRAFT
// ============================================================================

export const createPackDraft = async (tenantId, specialtyId, { name, description, createdByUid }) => {
  const db = getDb();
  const packId = generatePackId();
  const versionId = '1.0.0';
  const packDocId = `${packId}__${versionId}`;
  const path = PATHS.pack(tenantId, specialtyId, packDocId);

  const packDoc = {
    packId,
    versionId,
    tenantId,
    specialtyId,
    status: PACK_STATUS.DRAFT,
    name: name || 'Untitled Pack',
    description: description || '',
    createdByUid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),

    // Engine data (empty defaults — builder fills these)
    fields: [],
    redFlags: [],
    intraSpecialtyRedirectFlags: [],
    crossSpecialtyRedirectFlags: [],
    notYetNextSteps: [],
    patientMessageTemplates: {
      urgent: '',
      routine: '',
      not_yet: '',
    },
    telehealthEnabled: true,
    escalationOptions: [],
    attachmentRequirements: [],

    // Metadata block (for normalizePackDoc compatibility)
    pack: null,
  };

  await setDoc(doc(db, path), packDoc);
  return { packDocId, packId, versionId };
};

// ============================================================================
// UPDATE DRAFT (partial updates to any section)
// ============================================================================

export const updatePackDraft = async (tenantId, specialtyId, packDocId, updates) => {
  const db = getDb();
  const path = PATHS.pack(tenantId, specialtyId, packDocId);

  const ref = doc(db, path);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Pack not found: ${packDocId}`);
  if (snap.data().status !== PACK_STATUS.DRAFT) {
    throw new Error('Only draft packs can be edited');
  }

  const safeUpdates = { ...updates, updatedAt: serverTimestamp() };
  delete safeUpdates.packId;
  delete safeUpdates.tenantId;
  delete safeUpdates.specialtyId;
  delete safeUpdates.createdByUid;
  delete safeUpdates.createdAt;
  delete safeUpdates.status;

  await updateDoc(ref, safeUpdates);
};

// ============================================================================
// PUBLISH (draft → active)
// ============================================================================

export const publishPack = async (tenantId, specialtyId, packDocId) => {
  const db = getDb();
  const path = PATHS.pack(tenantId, specialtyId, packDocId);
  const ref = doc(db, path);
  const snap = await getDoc(ref);

  if (!snap.exists()) throw new Error(`Pack not found: ${packDocId}`);
  if (snap.data().status !== PACK_STATUS.DRAFT) {
    throw new Error('Only draft packs can be published');
  }

  await updateDoc(ref, {
    status: PACK_STATUS.ACTIVE,
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

// ============================================================================
// UPDATE ACTIVE PACK (in-place, same version)
// ============================================================================

export const updateActivePack = async (tenantId, specialtyId, packDocId, updates) => {
  const db = getDb();
  const path = PATHS.pack(tenantId, specialtyId, packDocId);

  const ref = doc(db, path);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Pack not found: ${packDocId}`);
  if (snap.data().status !== PACK_STATUS.ACTIVE) {
    throw new Error('Only active packs can be updated in-place');
  }

  const safeUpdates = { ...updates, updatedAt: serverTimestamp() };
  delete safeUpdates.packId;
  delete safeUpdates.tenantId;
  delete safeUpdates.specialtyId;
  delete safeUpdates.createdByUid;
  delete safeUpdates.createdAt;
  delete safeUpdates.status;

  await updateDoc(ref, safeUpdates);
};

// ============================================================================
// PUBLISH NEW VERSION (active → new active version; prior active → archived)
// ----------------------------------------------------------------------------
// Creates a NEW immutable version document `{packId}__{newVersionId}` carrying
// the edited content, and atomically archives the prior active version. The
// logical packId, tenantId, specialtyId, and Firestore parent path are
// preserved. History is never overwritten or deleted.
//
// Atomicity + guards via runTransaction:
//   - new version doc must NOT already exist (no overwrite of history)
//   - prior doc must still be ACTIVE (guards a double-publish race)
// A pre-flight active-count check fails fast if the pack already has more than
// one active version (client transactions cannot run queries).
//
// Returns { newPackDocId, newVersionId }.
// ============================================================================

export const publishNewPackVersion = async (
  tenantId,
  specialtyId,
  currentPackDocId,
  editedContent,
  { createdByUid, requestedVersionId } = {},
) => {
  const db = getDb();

  // Derive logical packId + CURRENT version authoritatively from the doc id
  // (`{packId}__{versionId}`). The current version must NOT come from
  // editedContent.versionId — that field is user-editable in the builder and
  // carries the *requested* new version, not the active one. The doc id is the
  // source of truth for the version being superseded.
  const sepIdx = currentPackDocId.lastIndexOf('__');
  const packId = sepIdx > 0 ? currentPackDocId.slice(0, sepIdx) : (editedContent?.packId || currentPackDocId);
  const currentVersionId = sepIdx > 0 ? currentPackDocId.slice(sepIdx + 2) : (editedContent?.versionId || '');
  if (!packId) {
    throw new Error('publishNewPackVersion: could not determine the pack id');
  }
  if (!parseSemver(currentVersionId)) {
    throw new Error(`publishNewPackVersion: current version "${currentVersionId}" is not valid semver`);
  }

  // Pre-flight: exactly ONE active doc must exist for this logical packId.
  const activePacks = await getPacksBySpecialty(tenantId, specialtyId, PACK_STATUS.ACTIVE);
  const activeForPack = activePacks.filter(
    (p) => (p.packId || (p.id ? p.id.split('__')[0] : null)) === packId,
  );
  if (activeForPack.length === 0) {
    throw new Error('publishNewPackVersion: no active version found for this pack');
  }
  if (activeForPack.length > 1) {
    throw new Error(
      'publishNewPackVersion: multiple active versions already exist for this pack — resolve before publishing a new version',
    );
  }

  // Compute the new version + doc id.
  const newVersionId = selectNewVersionId(currentVersionId, requestedVersionId);
  const newPackDocId = `${packId}__${newVersionId}`;
  const oldRef = doc(db, PATHS.pack(tenantId, specialtyId, currentPackDocId));
  const newRef = doc(db, PATHS.pack(tenantId, specialtyId, newPackDocId));

  // Build the new version body from the edited content. Strip identity /
  // lifecycle / lineage fields we set explicitly; preserve all authored content.
  const content = { ...(editedContent || {}) };
  [
    'id', 'packDocId', 'createdAt', 'updatedAt', 'publishedAt', 'archivedAt',
    'status', 'packId', 'versionId', 'tenantId', 'specialtyId', 'createdByUid',
    'supersedesVersionId', 'supersedesPackDocId',
    'supersededByVersionId', 'supersededByPackDocId',
  ].forEach((k) => { delete content[k]; });

  await runTransaction(db, async (tx) => {
    const newSnap = await tx.get(newRef);
    if (newSnap.exists()) {
      throw new Error(`publishNewPackVersion: version ${newVersionId} already exists`);
    }
    const oldSnap = await tx.get(oldRef);
    if (!oldSnap.exists()) {
      throw new Error('publishNewPackVersion: current pack version no longer exists');
    }
    if (oldSnap.data().status !== PACK_STATUS.ACTIVE) {
      throw new Error('publishNewPackVersion: current pack version is no longer active — reload and retry');
    }

    tx.set(newRef, {
      ...content,
      packId,
      versionId: newVersionId,
      tenantId,
      specialtyId,
      status: PACK_STATUS.ACTIVE,
      createdByUid: createdByUid || oldSnap.data().createdByUid || null,
      supersedesVersionId: currentVersionId,
      supersedesPackDocId: currentPackDocId,
      createdAt: serverTimestamp(),
      publishedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    tx.update(oldRef, {
      status: PACK_STATUS.ARCHIVED,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      supersededByVersionId: newVersionId,
      supersededByPackDocId: newPackDocId,
    });
  });

  return { newPackDocId, newVersionId };
};

// ============================================================================
// ARCHIVE (active → archived)
// ============================================================================

export const archivePack = async (tenantId, specialtyId, packDocId) => {
  const db = getDb();
  const path = PATHS.pack(tenantId, specialtyId, packDocId);
  const ref = doc(db, path);
  const snap = await getDoc(ref);

  if (!snap.exists()) throw new Error(`Pack not found: ${packDocId}`);
  if (snap.data().status !== PACK_STATUS.ACTIVE) {
    throw new Error('Only active packs can be archived');
  }

  await updateDoc(ref, {
    status: PACK_STATUS.ARCHIVED,
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

// ============================================================================
// READ — single pack
// ============================================================================

export const getPackDraft = async (tenantId, specialtyId, packDocId) => {
  const db = getDb();
  const path = PATHS.pack(tenantId, specialtyId, packDocId);
  const snap = await getDoc(doc(db, path));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

// ============================================================================
// LIST — packs for specialist's specialties
// ============================================================================

export const getPacksForSpecialist = async (tenantId, specialtyIds) => {
  const db = getDb();
  const results = [];

  for (const specId of specialtyIds) {
    const colPath = `tenants/${tenantId}/specialties/${specId}/packs`;
    const colRef = collection(db, colPath);
    const q = query(colRef, orderBy('updatedAt', 'desc'));
    const snap = await getDocs(q);
    snap.forEach((d) => {
      results.push({ id: d.id, specialtyId: specId, ...d.data() });
    });
  }

  return results;
};

// ============================================================================
// LIST — packs for a single specialty (all statuses or filtered)
// ============================================================================

export const getPacksBySpecialty = async (tenantId, specialtyId, statusFilter = null) => {
  const db = getDb();
  const colPath = `tenants/${tenantId}/specialties/${specialtyId}/packs`;
  const colRef = collection(db, colPath);

  let q;
  if (statusFilter) {
    q = query(colRef, where('status', '==', statusFilter), orderBy('updatedAt', 'desc'));
  } else {
    q = query(colRef, orderBy('updatedAt', 'desc'));
  }

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, specialtyId, ...d.data() }));
};

// ============================================================================
// LIST — all versions of one logical pack (read-only; for Version History)
// ----------------------------------------------------------------------------
// Returns RAW docs (all statuses) for a single logical packId so lineage/actor
// fields (createdByUid, publishedAt, archivedAt, supersedes*/supersededBy*) are
// preserved — unlike the normalized getPacks() in store.firebase.js which strips
// them. Read-only; reuses getPacksBySpecialty (single orderBy, no extra index).
// ============================================================================

export const getPackVersions = async (tenantId, specialtyId, packId) => {
  const all = await getPacksBySpecialty(tenantId, specialtyId, null);
  return all.filter((p) => {
    const logicalPackId = p.packId || (p.id ? p.id.split('__')[0] : null);
    return logicalPackId === packId;
  });
};

// ============================================================================
// DELETE DRAFT (only drafts can be deleted)
// ============================================================================

export const deletePackDraft = async (tenantId, specialtyId, packDocId) => {
  const db = getDb();
  const path = PATHS.pack(tenantId, specialtyId, packDocId);
  const ref = doc(db, path);
  const snap = await getDoc(ref);

  if (!snap.exists()) throw new Error(`Pack not found: ${packDocId}`);
  if (snap.data().status !== PACK_STATUS.DRAFT) {
    throw new Error('Only draft packs can be deleted');
  }

  await deleteDoc(ref);
};

export { PACK_STATUS };
